"use client";

import { useEffect, useState } from "react";
import Link from "next/link";

interface RecentSend {
  id: number;
  toEmail: string;
  subject: string;
  status: string;
  failureReason?: string;
  createdAt: string;
}

interface OnboardingDomain {
  id: number;
  domain: string;
  verified: boolean;
  dkimValidated: boolean;
}

interface OnboardingConfig {
  id: number;
  fromEmail: string;
  siteId: number | null;
  verified: boolean;
}

interface OnboardingSite {
  id: number;
  name: string;
  domain: string | null;
}

interface OverviewData {
  contacts: number;
  emailsSentThisMonth: number;
  costThisMonth: number;
  creditBalance: number;
  plan: string;
  bounceRate7d: string;
  complaintRate7d: string;
  sendingStatus: string;
  trustLevel: string;
  dailySendLimit: number;
  hourlySendLimit: number;
  pauseReason: string | null;
  siteCount: number;
  apiKeyCount: number;
  sendingConfigCount: number;
  brandingEnabled: boolean;
  recentSends: RecentSend[];
  onboarding?: {
    domains: OnboardingDomain[];
    sendingConfigs: OnboardingConfig[];
    sites: OnboardingSite[];
  };
}

interface SiteInfo {
  id: number;
  name: string;
}

interface SendingConfigInfo {
  id: number;
  provider: string;
  fromEmail: string;
  fromName: string;
}

export default function DashboardPage() {
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [brandingDismissed, setBrandingDismissed] = useState(false);
  const [brandingEnabling, setBrandingEnabling] = useState(false);
  const [learnDismissed, setLearnDismissed] = useState(true);

  // Send test email state
  const [sites, setSites] = useState<SiteInfo[]>([]);
  const [configs, setConfigs] = useState<SendingConfigInfo[]>([]);
  const [testTo, setTestTo] = useState("");
  const [testSubject, setTestSubject] = useState("Test email from PushMail");
  const [testBody, setTestBody] = useState("<h1>Hello!</h1><p>This is a test email sent from PushMail.</p>");
  const [testSiteId, setTestSiteId] = useState<number | null>(null);
  const [testConfigId, setTestConfigId] = useState<number | null>(null);
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState<{ type: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    if (localStorage.getItem("pushmail:learn-dismissed") !== "true") {
      setLearnDismissed(false);
    }
  }, []);

  useEffect(() => {
    Promise.all([
      fetch("/api/v1/dashboard/overview").then((r) => r.json() as Promise<{ data?: OverviewData }>),
      fetch("/api/v1/sites").then((r) => r.json() as Promise<{ data?: { sites: SiteInfo[] } }>),
      fetch("/api/v1/sending-configs").then((r) => r.json() as Promise<{ data?: { configs: SendingConfigInfo[] } }>),
    ])
      .then(([overviewJson, sitesJson, configsJson]) => {
        if (overviewJson.data) setData(overviewJson.data);
        if (sitesJson.data?.sites) {
          setSites(sitesJson.data.sites);
          if (sitesJson.data.sites.length > 0) setTestSiteId(sitesJson.data.sites[0].id);
        }
        if (configsJson.data?.configs) {
          setConfigs(configsJson.data.configs);
          if (configsJson.data.configs.length > 0) setTestConfigId(configsJson.data.configs[0].id);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  async function handleSendTest() {
    if (!testTo.trim() || !testSubject.trim() || !testSiteId) {
      setTestResult({ type: "error", text: "To, subject, and site are required" });
      return;
    }
    setTestSending(true);
    setTestResult(null);
    try {
      const payload: Record<string, unknown> = {
        to: testTo.trim(),
        subject: testSubject.trim(),
        html: testBody,
        siteId: testSiteId,
      };
      if (testConfigId) payload.sendingConfigId = testConfigId;

      const res = await fetch("/api/v1/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = (await res.json()) as { data?: { sendId: number; status: string }; error?: string };
      if (!res.ok) {
        setTestResult({ type: "error", text: json.error || "Failed to send" });
        return;
      }
      setTestResult({ type: "success", text: `Email queued (send #${json.data?.sendId})` });
      setTestTo("");
    } catch {
      setTestResult({ type: "error", text: "Failed to send test email" });
    } finally {
      setTestSending(false);
    }
  }

  if (loading || !data) {
    return (
      <div>
        <h1 className="mb-6 text-2xl font-bold text-zinc-900 dark:text-white">Dashboard</h1>
        <div aria-live="polite">
          <p className="text-sm text-zinc-400">Loading...</p>
        </div>
      </div>
    );
  }

  const bounceRateNum = parseFloat(data.bounceRate7d);

  const checklist = [
    {
      label: "Create a brand",
      hint: "e.g. SuperPowerResume, InventiveHQ — you can add domains at the same time",
      done: data.siteCount > 0,
      href: "/dashboard/settings",
    },
    {
      label: "Set up a sending configuration",
      hint: "Your from address and email provider — use managed or bring your own key",
      done: data.sendingConfigCount > 0,
      href: "/dashboard/settings",
    },
    {
      label: "Generate an API key",
      done: data.apiKeyCount > 0,
      href: "/dashboard/api-keys",
    },
    {
      label: "Add contacts",
      done: data.contacts > 0,
      href: "/dashboard/contacts",
    },
    {
      label: "Send your first email",
      done: data.emailsSentThisMonth > 0,
      href: "/docs/quickstart",
    },
  ];

  const allDone = checklist.every((item) => item.done);

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold text-zinc-900 dark:text-white">Dashboard</h1>

      {/* Health status banner */}
      {data.sendingStatus !== "active" && (
        <div
          className={`mb-6 rounded-lg border p-4 ${
            data.sendingStatus === "paused" || data.sendingStatus === "suspended"
              ? "border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950"
              : "border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950"
          }`}
        >
          <div className="flex items-center gap-2">
            <span
              className={`text-sm font-semibold ${
                data.sendingStatus === "paused" || data.sendingStatus === "suspended"
                  ? "text-red-800 dark:text-red-300"
                  : "text-amber-800 dark:text-amber-300"
              }`}
            >
              Sending status: {data.sendingStatus}
            </span>
          </div>
          {data.pauseReason && (
            <p
              className={`mt-1 text-sm ${
                data.sendingStatus === "paused" || data.sendingStatus === "suspended"
                  ? "text-red-700 dark:text-red-400"
                  : "text-amber-700 dark:text-amber-400"
              }`}
            >
              {data.pauseReason}
            </p>
          )}
        </div>
      )}

      {/* Branding discount opt-in banner */}
      {!data.brandingEnabled && !brandingDismissed && (
        <div className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-950">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">
                Save 15% on every email
              </p>
              <p className="mt-1 text-sm text-emerald-700 dark:text-emerald-400">
                Add a small &ldquo;Sent with PushMail&rdquo; footer to your emails and get 15% off every send. You can turn it off anytime in Settings.
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                onClick={async () => {
                  setBrandingEnabling(true);
                  try {
                    const res = await fetch("/api/v1/org/branding", {
                      method: "PUT",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ enabled: true }),
                    });
                    if (res.ok) {
                      setData((prev) => prev ? { ...prev, brandingEnabled: true } : prev);
                    }
                  } finally {
                    setBrandingEnabling(false);
                  }
                }}
                disabled={brandingEnabling}
                className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {brandingEnabling ? "Enabling..." : "Enable"}
              </button>
              <button
                onClick={() => setBrandingDismissed(true)}
                className="rounded-md px-2 py-1.5 text-sm text-emerald-600 hover:bg-emerald-100 dark:text-emerald-400 dark:hover:bg-emerald-900"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Contacts" value={data.contacts.toLocaleString()} />
        <StatCard label="Emails Sent" sublabel="this month" value={data.emailsSentThisMonth.toLocaleString()} />
        <StatCard
          label="Bounce Rate"
          sublabel="7 days"
          value={data.bounceRate7d}
          valueClassName={bounceRateNum > 3 ? "text-red-600 dark:text-red-400" : undefined}
        />
        <StatCard label="Credits" value={`$${(data.creditBalance / 100).toFixed(2)}`} />
      </div>

      {/* Onboarding reminders */}
      {data.onboarding && <OnboardingReminders onboarding={data.onboarding} />}

      {/* Learn section */}
      {!learnDismissed && (
        <div className="mt-8">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">Learn</h2>
            <button
              onClick={() => {
                setLearnDismissed(true);
                localStorage.setItem("pushmail:learn-dismissed", "true");
              }}
              className="text-xs text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
            >
              Dismiss
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <LearnCard
              title="Campaigns vs. Sequences"
              description="When to use one-time blasts vs. automated drip flows — and how they work together."
              href="/blog/campaigns-vs-sequences"
              tag="Overview"
            />
            <LearnCard
              title="Guide to Email Campaigns"
              description="Everything about sending campaigns — audience, scheduling, deliverability, and tracking."
              href="/blog/guide-to-email-campaigns"
              tag="Campaigns"
            />
            <LearnCard
              title="Guide to Drip Sequences"
              description="Build automated sequences that onboard users, nurture leads, and re-engage contacts."
              href="/blog/guide-to-drip-sequences"
              tag="Sequences"
            />
          </div>
        </div>
      )}

      {/* Quick start checklist */}
      {!allDone && (
        <div className="mt-8">
          <h2 className="mb-3 text-lg font-semibold text-zinc-900 dark:text-white">Quick Start</h2>
          <div className="rounded-lg border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
            {checklist.map((item, i) =>
              item.done ? (
                <div
                  key={item.label}
                  className={`flex items-center gap-3 px-4 py-3 ${
                    i !== checklist.length - 1 ? "border-b border-zinc-100 dark:border-zinc-800" : ""
                  }`}
                >
                  <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
                    <svg
                      className="h-3 w-3 text-green-600 dark:text-green-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-sm text-zinc-400 line-through">{item.label}</span>
                </div>
              ) : (
                <Link
                  key={item.label}
                  href={item.href}
                  className={`flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 ${
                    i !== checklist.length - 1 ? "border-b border-zinc-100 dark:border-zinc-800" : ""
                  }`}
                >
                  <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 border-zinc-300 text-zinc-400 dark:border-zinc-600">
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-medium text-zinc-900 dark:text-white">{item.label}</span>
                    {item.hint && (
                      <p className="mt-0.5 text-xs text-zinc-400 dark:text-zinc-500">{item.hint}</p>
                    )}
                  </div>
                  <svg className="ml-auto h-4 w-4 shrink-0 text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              )
            )}
          </div>
        </div>
      )}

      {/* Send Test Email */}
      {sites.length > 0 && configs.length > 0 && (
        <div className="mt-8">
          <h2 className="mb-3 text-lg font-semibold text-zinc-900 dark:text-white">Send Test Email</h2>
          <div className="rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
            {testResult && (
              <div className={`mb-3 rounded-md px-3 py-2 text-sm ${
                testResult.type === "success"
                  ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400"
                  : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
              }`}>
                {testResult.text}
              </div>
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="test-to" className="mb-1 block text-xs font-medium text-zinc-500 dark:text-zinc-400">To</label>
                <input
                  id="test-to"
                  type="email"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder="recipient@example.com"
                  className="w-full rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-sm text-zinc-900 placeholder-zinc-400 focus:border-zinc-400 focus:outline-none dark:border-zinc-700 dark:bg-zinc-800 dark:text-white dark:placeholder-zinc-500"
                />
              </div>
              <div>
                <label htmlFor="test-subject" className="mb-1 block text-xs font-medium text-zinc-500 dark:text-zinc-400">Subject</label>
                <input
                  id="test-subject"
                  type="text"
                  value={testSubject}
                  onChange={(e) => setTestSubject(e.target.value)}
                  className="w-full rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-sm text-zinc-900 placeholder-zinc-400 focus:border-zinc-400 focus:outline-none dark:border-zinc-700 dark:bg-zinc-800 dark:text-white dark:placeholder-zinc-500"
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="test-html-body" className="mb-1 block text-xs font-medium text-zinc-500 dark:text-zinc-400">HTML Body</label>
                <textarea
                  id="test-html-body"
                  value={testBody}
                  onChange={(e) => setTestBody(e.target.value)}
                  rows={3}
                  className="w-full rounded-md border border-zinc-200 bg-white px-3 py-1.5 font-mono text-sm text-zinc-900 placeholder-zinc-400 focus:border-zinc-400 focus:outline-none dark:border-zinc-700 dark:bg-zinc-800 dark:text-white dark:placeholder-zinc-500"
                />
              </div>
              <div className="flex items-end gap-3 sm:col-span-2">
                {sites.length > 1 && (
                  <div className="flex-1">
                    <label htmlFor="test-site" className="mb-1 block text-xs font-medium text-zinc-500 dark:text-zinc-400">Site</label>
                    <select
                      id="test-site"
                      value={testSiteId || ""}
                      onChange={(e) => setTestSiteId(Number(e.target.value))}
                      className="w-full rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-sm text-zinc-900 focus:border-zinc-400 focus:outline-none dark:border-zinc-700 dark:bg-zinc-800 dark:text-white"
                    >
                      {sites.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                {configs.length > 1 && (
                  <div className="flex-1">
                    <label htmlFor="test-config" className="mb-1 block text-xs font-medium text-zinc-500 dark:text-zinc-400">Sending Config</label>
                    <select
                      id="test-config"
                      value={testConfigId || ""}
                      onChange={(e) => setTestConfigId(Number(e.target.value))}
                      className="w-full rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-sm text-zinc-900 focus:border-zinc-400 focus:outline-none dark:border-zinc-700 dark:bg-zinc-800 dark:text-white"
                    >
                      {configs.map((c) => (
                        <option key={c.id} value={c.id}>{c.fromEmail} ({c.provider})</option>
                      ))}
                    </select>
                  </div>
                )}
                <button
                  onClick={handleSendTest}
                  disabled={testSending || !testTo.trim()}
                  className="rounded-md bg-zinc-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-zinc-800 disabled:opacity-50 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
                >
                  {testSending ? "Sending..." : "Send"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No sending config banner */}
      {sites.length > 0 && configs.length === 0 && (
        <div className="mt-8">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-5 dark:border-amber-800 dark:bg-amber-950">
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900">
                <svg className="h-4 w-4 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                  Set up a sending configuration
                </p>
                <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">
                  You need a sending configuration to send emails. This tells PushMail your &ldquo;from&rdquo; address (e.g. hello@yourdomain.com) and which email provider to use. You can use PushMail&apos;s managed infrastructure or bring your own API key.
                </p>
                <Link
                  href="/dashboard/settings"
                  className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700"
                >
                  Configure now
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent sends */}
      <div className="mt-8">
        <h2 className="mb-3 text-lg font-semibold text-zinc-900 dark:text-white">Recent Sends</h2>
        {data.recentSends.length === 0 ? (
          <div className="rounded-lg border border-zinc-200 bg-white p-6 text-center dark:border-zinc-800 dark:bg-zinc-900">
            <p className="text-sm text-zinc-500 dark:text-zinc-400">No emails sent yet.</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-100 dark:border-zinc-800">
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-zinc-500 dark:text-zinc-400">To</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-zinc-500 dark:text-zinc-400">Subject</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-zinc-500 dark:text-zinc-400">Status</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-zinc-500 dark:text-zinc-400">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.recentSends.map((send) => (
                  <tr
                    key={send.id}
                    onClick={() => { window.location.href = `/dashboard/logs/${send.id}`; }}
                    className="cursor-pointer border-b border-zinc-50 last:border-0 hover:bg-zinc-50 dark:border-zinc-800/50 dark:hover:bg-zinc-800/40"
                  >
                    <td className="px-4 py-3 text-zinc-900 dark:text-zinc-100">
                      <Link href={`/dashboard/logs/${send.id}`} className="hover:underline">
                        {send.toEmail}
                      </Link>
                    </td>
                    <td className="max-w-[200px] truncate px-4 py-3 text-zinc-700 dark:text-zinc-300">
                      {send.subject}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge
                        status={send.status}
                        reason={send.failureReason || (send.subject.startsWith("Blocked:") ? send.subject : undefined)}
                      />
                    </td>
                    <td className="px-4 py-3 text-zinc-500 dark:text-zinc-400">{formatDate(send.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// -- Onboarding Reminders -----------------------------------------------------

function OnboardingReminders({
  onboarding,
}: {
  onboarding: NonNullable<OverviewData["onboarding"]>;
}) {
  const { domains, sendingConfigs, sites } = onboarding;

  // Build list of actionable items
  const items: Array<{
    key: string;
    icon: "domain" | "dns" | "config" | "site";
    title: string;
    description: string;
    href: string;
    cta: string;
    severity: "warning" | "info";
  }> = [];

  // Sites without domains
  const sitesWithoutDomain = sites.filter((s) => !s.domain);
  for (const site of sitesWithoutDomain) {
    items.push({
      key: `site-no-domain-${site.id}`,
      icon: "site",
      title: `${site.name} has no domain`,
      description: `Set up a sending domain for ${site.name} so your emails are authenticated and delivered reliably.`,
      href: "/dashboard/domains/setup",
      cta: "Set up domain",
      severity: "warning",
    });
  }

  // Domains with pending ownership verification
  const unverifiedDomains = domains.filter((d) => !d.verified);
  for (const domain of unverifiedDomains) {
    items.push({
      key: `domain-unverified-${domain.id}`,
      icon: "dns",
      title: `${domain.domain} — ownership not verified`,
      description: "Add the TXT record to your DNS to verify domain ownership. We check automatically every minute.",
      href: `/dashboard/domains?expand=${domain.domain}`,
      cta: "View DNS records",
      severity: "warning",
    });
  }

  // Domains verified but DKIM not validated
  const dkimPending = domains.filter((d) => d.verified && !d.dkimValidated);
  for (const domain of dkimPending) {
    items.push({
      key: `domain-dkim-${domain.id}`,
      icon: "dns",
      title: `${domain.domain} — DKIM not validated`,
      description: "Domain ownership is verified but DKIM records are still pending. Add the CNAME records to enable email authentication.",
      href: `/dashboard/domains?expand=${domain.domain}`,
      cta: "View DNS records",
      severity: "info",
    });
  }

  // Sending configs not verified
  const unverifiedConfigs = sendingConfigs.filter((c) => !c.verified);
  for (const config of unverifiedConfigs) {
    items.push({
      key: `config-unverified-${config.id}`,
      icon: "config",
      title: `Sending config ${config.fromEmail} — not verified`,
      description: "Send a test email to verify this sending configuration works correctly.",
      href: "/dashboard/settings",
      cta: "Verify config",
      severity: "info",
    });
  }

  // No domains at all but has sites
  if (domains.length === 0 && sites.length > 0) {
    // Already covered by sitesWithoutDomain, but add a general nudge if somehow missed
  }

  // No sending configs at all (already handled by existing banner, skip)

  if (items.length === 0) return null;

  return (
    <div className="mt-8">
      <h2 className="mb-3 text-lg font-semibold text-zinc-900 dark:text-white">
        Complete Your Setup
      </h2>
      <div className="space-y-3">
        {items.map((item) => (
          <div
            key={item.key}
            className={`rounded-lg border p-4 ${
              item.severity === "warning"
                ? "border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950"
                : "border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950"
            }`}
          >
            <div className="flex items-start gap-3">
              <div
                className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                  item.severity === "warning"
                    ? "bg-amber-100 dark:bg-amber-900"
                    : "bg-blue-100 dark:bg-blue-900"
                }`}
              >
                {item.icon === "dns" || item.icon === "domain" ? (
                  <svg
                    className={`h-3.5 w-3.5 ${
                      item.severity === "warning"
                        ? "text-amber-600 dark:text-amber-400"
                        : "text-blue-600 dark:text-blue-400"
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                ) : item.icon === "config" ? (
                  <svg
                    className={`h-3.5 w-3.5 ${
                      item.severity === "warning"
                        ? "text-amber-600 dark:text-amber-400"
                        : "text-blue-600 dark:text-blue-400"
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                ) : (
                  <svg
                    className={`h-3.5 w-3.5 ${
                      item.severity === "warning"
                        ? "text-amber-600 dark:text-amber-400"
                        : "text-blue-600 dark:text-blue-400"
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm font-medium ${
                    item.severity === "warning"
                      ? "text-amber-800 dark:text-amber-300"
                      : "text-blue-800 dark:text-blue-300"
                  }`}
                >
                  {item.title}
                </p>
                <p
                  className={`mt-0.5 text-sm ${
                    item.severity === "warning"
                      ? "text-amber-700 dark:text-amber-400"
                      : "text-blue-700 dark:text-blue-400"
                  }`}
                >
                  {item.description}
                </p>
              </div>
              <Link
                href={item.href}
                className={`shrink-0 rounded-md px-3 py-1.5 text-sm font-medium text-white ${
                  item.severity === "warning"
                    ? "bg-amber-600 hover:bg-amber-700"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {item.cta}
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// -- Local components ---------------------------------------------------------

function StatCard({
  label,
  sublabel,
  value,
  valueClassName,
}: {
  label: string;
  sublabel?: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
      <div className="text-xs font-medium text-zinc-500">
        {label}
        {sublabel && <span className="ml-1 text-zinc-400">({sublabel})</span>}
      </div>
      <div className={`mt-1 text-2xl font-bold ${valueClassName ?? "text-zinc-900 dark:text-white"}`}>{value}</div>
    </div>
  );
}

function StatusBadge({ status, reason }: { status: string; reason?: string }) {
  let color: string;
  switch (status) {
    case "sent":
      color = "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300";
      break;
    case "delivered":
      color = "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300";
      break;
    case "bounced":
    case "failed":
      color = "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300";
      break;
    case "queued":
    default:
      color = "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400";
      break;
  }

  return (
    <span
      aria-label={reason || undefined}
      className={`inline-block cursor-default rounded-full px-2 py-0.5 text-xs font-medium ${color}`}
    >
      {status}
    </span>
  );
}

function LearnCard({
  title,
  description,
  href,
  tag,
}: {
  title: string;
  description: string;
  href: string;
  tag: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="group rounded-lg border border-zinc-200 bg-white p-4 transition-colors hover:border-zinc-300 dark:border-zinc-800 dark:bg-zinc-900 dark:hover:border-zinc-700"
    >
      <span className="mb-2 inline-block rounded-full border border-zinc-200 px-2 py-0.5 text-[10px] font-medium text-zinc-500 dark:border-zinc-700 dark:text-zinc-400">
        {tag}
      </span>
      <h3 className="text-sm font-semibold text-zinc-900 group-hover:text-zinc-600 dark:text-white dark:group-hover:text-zinc-300">
        {title}
      </h3>
      <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{description}</p>
    </a>
  );
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
